import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Star, Building2, UtensilsCrossed, Plane, ChefHat, Building, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import ProductCard from '@/components/shared/ProductCard';
import InquiryModal from '@/components/shared/InquiryModal';
import ProductDetailModal from '@/components/shared/ProductDetailModal';
import { getCategories, getProducts, getTestimonials, downloadCatalogue, getHeroSlides } from '@/lib/api';
import SEO from '@/components/shared/SEO';

// Default hero slides (fallback when API returns empty)
const defaultHeroSlides = [
  {
    id: 1,
    title: "Elevating Hospitality with Quality Supplies",
    subtitle: "Comprehensive product solutions designed to support hotels, restaurants, and enterprise facilities.",
    image: "/assets/images/products/premium-hospitality.webp",
    category: "Premium Hospitality"
  },
  {
    id: 2,
    title: "Premium Guest Amenities",
    subtitle: "Complete range of toiletries, vanity kits, dental kits, shaving kits, and comfort essentials for your guests.",
    image: "/assets/images/products/premium-guest-amenities.webp",
    category: "Guest Amenities"
  },
  {
    id: 3,
    title: "Eco-Friendly Packaging Solutions",
    subtitle: "Sustainable bagasse, paper containers, corn starch products, and biodegradable packaging for modern hospitality.",
    image: "/assets/images/products/eco-friendly-packaging.webp",
    category: "Eco Packaging"
  },
  {
    id: 4,
    title: "Professional Cleaning Solutions",
    subtitle: "Quality cleaning supplies, chemicals, mops, and equipment for spotless facilities.",
    image: "/assets/images/products/professional-cleaning.webp",
    category: "Housekeeping"
  }
];

// Premium SVG Icons for categories
const CategoryIcon = ({ type, className }) => {
  const icons = {
    amenities: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M20 7h-9M14 17H5M17 17a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM7 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    toiletries: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M10 2h4v4h-4zM8 6h8v4a6 6 0 0 1-8 0V6zM10 14v6M14 14v6M8 22h8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    slippers: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4 16c0-4 2-6 6-6s6 2 6 6c0 2-2 4-6 4s-6-2-6-4z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 12V8M12 12V8" strokeLinecap="round" />
      </svg>
    ),
    leaf: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c1.5 0 3-.5 4.5-1.5C11 18 8 14 8 10c0-2 1-4 4-6 3-2 6-2 8-2 0 4-1 8-4 11" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 22V12" strokeLinecap="round" />
      </svg>
    ),
    package: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M21 8V16a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 8l9 4 9-4M12 12v6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    bakery: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 6c4.5 0 8 1.5 8 4v6c0 2.5-3.5 4-8 4s-8-1.5-8-4v-6c0-2.5 3.5-4 8-4z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 10c0 2.5 3.5 4 8 4s8-1.5 8-4" strokeLinecap="round" />
        <path d="M4 14c0 2.5 3.5 4 8 4s8-1.5 8-4" strokeLinecap="round" />
      </svg>
    ),
    paper: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    cleaning: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 3v7M9 7l3 3 3-3M5 14h14M7 14v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    chemical: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 3h6v5l4 9a2 2 0 0 1-2 3H7a2 2 0 0 1-2-3l4-9V3z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 3h6M7 15h10" strokeLinecap="round" />
      </svg>
    ),
    safety: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2L4 6v6c0 5.5 3.5 10.7 8 12 4.5-1.3 8-6.5 8-12V6l-8-4z" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    equipment: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.6.79 1.02 1.51 1.08H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    droplet: (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  };
  return icons[type] || icons.package;
};

// Map category IDs to icon types
const categoryIconMap = {
  'cat-amenities': 'amenities',
  'cat-eco-amenities': 'leaf',
  'cat-toiletries': 'droplet',
  'cat-slippers': 'slippers',
  'cat-eco-food': 'leaf',
  'cat-eco-disposables': 'leaf',
  'cat-bagasse': 'package',
  'cat-bakery': 'bakery',
  'cat-food-packaging': 'package',
  'cat-paper': 'paper',
  'cat-cleaning': 'cleaning',
  'cat-chemicals': 'chemical',
  'cat-safety': 'safety',
  'cat-equipment': 'equipment',
};

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailProduct, setDetailProduct] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [heroSlides, setHeroSlides] = useState(defaultHeroSlides);

  // Fetch dynamic hero slides
  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const res = await getHeroSlides();
        if (res.data && res.data.length > 0) {
          setHeroSlides(res.data.map(s => ({
            id: s.id,
            title: s.title,
            subtitle: s.subtitle,
            image: s.image_url,
            category: s.category,
          })));
        }
      } catch (err) {
        // Keep default slides on error
      }
    };
    fetchSlides();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes, testRes] = await Promise.all([
          getCategories(),
          getProducts({ featured: true }),
          getTestimonials(),
        ]);
        setCategories(catRes.data);
        // Enrich products with category names
        const catMap = {};
        catRes.data.forEach(c => { catMap[c.id] = c.name; });
        const enriched = prodRes.data.slice(0, 8).map(p => ({
          ...p,
          category_name: catMap[p.category_id] || ''
        }));
        setFeaturedProducts(enriched);
        setTestimonials(testRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-rollover testimonials
  useEffect(() => {
    if (testimonials.length <= 1) return;
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const industries = [
    { name: 'Hotels', icon: Building2, desc: 'Luxury hotels and resorts' },
    { name: 'Restaurants', icon: UtensilsCrossed, desc: 'Fine dining to quick service' },
    { name: 'Airlines', icon: Plane, desc: 'In-flight services' },
    { name: 'Catering', icon: ChefHat, desc: 'Event and corporate catering' },
    { name: 'Corporate', icon: Building, desc: 'Office housekeeping' },
  ];

  const features = [
    { icon: '🌿', title: 'Eco-Friendly', desc: 'Sustainable solutions' },
    { icon: '⭐', title: 'Premium Quality', desc: 'Top hospitality brands' },
    { icon: '🚚', title: 'Pan-India Delivery', desc: 'Nationwide shipping' },
    { icon: '🏆', title: '12+ Years', desc: 'Industry experience' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SEO />
      <Navbar />
      <main id="main-content">

      {/* Hero Section */}
      <section className="relative h-[85vh] md:h-screen overflow-hidden" data-testid="hero-section">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={currentSlide === 0 ? false : { opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="absolute inset-0"
          >
            <img
              src={heroSlides[currentSlide].image}
              alt={heroSlides[currentSlide].title}
              className="w-full h-full object-cover"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/40" />
          </motion.div>
        </AnimatePresence>

        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex items-center">
          <div className="max-w-2xl pt-16 md:pt-20">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={currentSlide === 0 ? false : { opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5 }}
              >
                <span className="inline-block px-4 py-1.5 bg-[#D4AF37] text-white text-xs font-semibold tracking-wider uppercase rounded mb-4 md:mb-6">
                  {heroSlides[currentSlide].category}
                </span>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight" style={{ fontFamily: 'Manrope' }}>
                  {heroSlides[currentSlide].title}
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-white/80 mb-6 md:mb-8 leading-relaxed">
                  {heroSlides[currentSlide].subtitle}
                </p>
                <div className="flex flex-wrap gap-3 md:gap-4">
                  <Link to="/products">
                    <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8942D] text-white rounded-none px-6 md:px-8 h-10 md:h-12 font-semibold text-sm md:text-base" data-testid="hero-explore-btn">
                      Explore Products <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-none px-6 md:px-8 h-10 md:h-12 font-semibold text-sm md:text-base" data-testid="hero-quote-btn">
                      Request Quote
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-12 left-6 md:left-12 z-20 flex items-center gap-6">
          <div className="flex items-center gap-3">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-1 transition-all duration-300 ${index === currentSlide ? 'w-12 bg-[#D4AF37]' : 'w-6 bg-white/40 hover:bg-white/60'}`}
                data-testid={`hero-dot-${index}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          <span className="text-white/60 text-sm font-medium">
            {String(currentSlide + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}
          </span>
        </div>

        {/* Navigation Arrows */}
        <div className="absolute bottom-12 right-6 md:right-12 z-20 flex gap-2">
          <button onClick={prevSlide} className="w-12 h-12 border border-white/30 flex items-center justify-center text-white hover:bg-white/10 transition-colors" data-testid="hero-prev-btn" aria-label="Previous slide">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button onClick={nextSlide} className="w-12 h-12 border border-white/30 flex items-center justify-center text-white hover:bg-white/10 transition-colors" data-testid="hero-next-btn" aria-label="Next slide">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-primary py-5 border-b border-primary/90" data-testid="features-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 justify-center md:justify-start">
                <span className="text-2xl">{feature.icon}</span>
                <div>
                  <p className="text-white font-semibold text-sm">{feature.title}</p>
                  <p className="text-white/60 text-xs">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories - Premium Design */}
      <section className="py-20 md:py-24 bg-gradient-to-b from-gray-50 to-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-3">Our Products</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              Product Categories
            </h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto mt-4" />
          </div>

          {/* Premium Categories Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
            {loading ? (
              Array(10).fill(0).map((_, i) => (
                <div key={i} className="aspect-[4/5] rounded-lg skeleton" />
              ))
            ) : (
              categories.map((category, idx) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.03 }}
                >
                  <Link
                    to={`/products/${category.id}`}
                    className="group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100"
                    data-testid={`category-card-${category.id}`}
                  >
                    {/* Image */}
                    <div className="relative aspect-square overflow-hidden bg-gray-100">
                      <img
                        src={category.image_url}
                        alt={category.name}
                        width="300"
                        height="300"
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                        decoding="async"
                      />
                      {/* Premium Icon Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                        <ArrowRight className="w-4 h-4 text-primary" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors duration-300">
                          <CategoryIcon
                            type={categoryIconMap[category.id] || 'package'}
                            className="w-5 h-5 text-primary group-hover:text-white transition-colors duration-300"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 text-sm leading-tight group-hover:text-primary transition-colors" style={{ fontFamily: 'Manrope' }}>
                            {category.name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">
                            {category.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button className="bg-primary hover:bg-primary/90 text-white rounded-none px-10 h-12 font-semibold" data-testid="view-all-categories-btn">
                View All Products <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 md:py-28 bg-white" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <p className="text-[#D4AF37] font-semibold text-xs tracking-[0.2em] uppercase mb-2">Trusted by 500+ Hotels</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#14532D]" style={{ fontFamily: 'Manrope' }}>
                Featured Products
              </h2>
              <p className="text-gray-500 mt-2 max-w-md text-sm">
                Handpicked products trusted by leading hotels and hospitality businesses across India.
              </p>
            </div>
            <Link to="/products">
              <Button variant="outline" className="border-[#14532D] text-[#14532D] hover:bg-[#14532D] hover:text-white rounded-sm font-semibold h-10 px-6 text-sm" data-testid="view-all-products-btn">
                View All Products <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {Array(4).fill(0).map((_, i) => (
                <div key={i} className="aspect-[3/4] rounded-xl bg-gray-100 animate-pulse" />
              ))}
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {featuredProducts.slice(0, 8).map((product, idx) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.4, delay: idx * 0.06 }}
                >
                  <ProductCard product={product} onInquiry={setSelectedProduct} onViewDetail={setDetailProduct} />
                </motion.div>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 md:py-24 bg-gray-50" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-3">Who We Serve</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
              Industries We Serve
            </h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {industries.map((industry, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link
                  to="/industries"
                  className="group block p-6 bg-white rounded-xl text-center hover:bg-primary transition-all duration-300 shadow-sm hover:shadow-lg"
                  data-testid={`industry-${industry.name.toLowerCase()}`}
                >
                  <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 transition-colors">
                    <industry.icon className="w-7 h-7 text-primary group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-white transition-colors" style={{ fontFamily: 'Manrope' }}>
                    {industry.name}
                  </h3>
                  <p className="text-xs text-gray-500 group-hover:text-white/80 mt-1 transition-colors">{industry.desc}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-24 bg-white overflow-hidden" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-14">
              <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-3">Testimonials</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
                What Our Clients Say
              </h2>
            </div>

            <div className="relative">
              <div className="flex transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                {testimonials.map((testimonial) => (
                  <div key={testimonial.id} className="w-full flex-shrink-0 px-4">
                    <div className="max-w-2xl mx-auto bg-gray-50 p-8 md:p-10 rounded-xl text-center">
                      <div className="flex items-center justify-center gap-1 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
                        ))}
                      </div>
                      <p className="text-gray-600 mb-6 italic leading-relaxed text-lg">"{testimonial.content}"</p>
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                          <span className="text-white font-semibold">{testimonial.name.charAt(0)}</span>
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900" style={{ fontFamily: 'Manrope' }}>{testimonial.name}</p>
                          <p className="text-sm text-gray-500">{testimonial.role}, {testimonial.company}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {testimonials.length > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {testimonials.map((_, idx) => (
                    <button key={idx} onClick={() => setActiveTestimonial(idx)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === activeTestimonial ? 'bg-primary w-8' : 'bg-gray-300'}`}
                      data-testid={`testimonial-dot-${idx}`}
                      aria-label={`Go to testimonial ${idx + 1}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Client Logos - Trusted Brands */}
      <section className="py-16 md:py-20 bg-[#0B1D0F] relative overflow-hidden" data-testid="clients-section">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 md:px-12 mb-12">
          <div className="text-center">
            <p className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-3">Our Clients</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              Trusted by Leading Brands
            </h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto mt-4 mb-4" />
            <p className="text-white/50 text-sm max-w-lg mx-auto">
              Proudly serving prestigious hotel chains and hospitality brands across India
            </p>
          </div>
        </div>

        {/* Row 1 - scrolls left */}
        <div className="relative mb-6">
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-[#0B1D0F] to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-[#0B1D0F] to-transparent z-10" />
          <div className="flex animate-scroll-left gap-8 md:gap-12 items-center">
            {[
              'radisson', 'ramada', 'taj', 'welcom-hotel', 'westin', 'hyatt-regency',
              'alila', 'ananta', 'clarks-amer', 'crowne-plaza',
              'radisson', 'ramada', 'taj', 'welcom-hotel', 'westin', 'hyatt-regency',
              'alila', 'ananta', 'clarks-amer', 'crowne-plaza',
            ].map((client, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 bg-white rounded-xl p-4 md:p-5 flex items-center justify-center hover:shadow-lg hover:shadow-white/10 transition-all duration-300 group"
              >
                <img
                  src={`/assets/images/clients/${client}.webp`}
                  alt={client.replace(/-/g, ' ')}
                  width="100"
                  height="48"
                  loading="lazy"
                  decoding="async"
                  className="h-10 md:h-12 w-auto opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Row 2 - scrolls right */}
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-[#0B1D0F] to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-[#0B1D0F] to-transparent z-10" />
          <div className="flex animate-scroll-right gap-8 md:gap-12 items-center">
            {[
              'fairmont', 'four-points', 'hilton', 'holiday-inn', 'jw-marriott',
              'le-meridien', 'leisure-inn', 'marriott', 'park-inn',
              'fairmont', 'four-points', 'hilton', 'holiday-inn', 'jw-marriott',
              'le-meridien', 'leisure-inn', 'marriott', 'park-inn',
            ].map((client, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 bg-white rounded-xl p-4 md:p-5 flex items-center justify-center hover:shadow-lg hover:shadow-white/10 transition-all duration-300 group"
              >
                <img
                  src={`/assets/images/clients/${client}.webp`}
                  alt={client.replace(/-/g, ' ')}
                  width="100"
                  height="48"
                  loading="lazy"
                  decoding="async"
                  className="h-10 md:h-12 w-auto opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-primary" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Ready to Partner With Us?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Get premium hospitality supplies at competitive prices. Request a quote today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-none px-8 h-12 font-semibold" data-testid="cta-quote-btn">
                Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href={downloadCatalogue()} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-none px-8 h-12 font-semibold" data-testid="cta-download-btn">
                <Download className="w-4 h-4 mr-2" /> Download Catalogue
              </Button>
            </a>
          </div>
        </div>
      </section>
      </main>

      <Footer />

      <ProductDetailModal
        product={detailProduct}
        isOpen={!!detailProduct}
        onClose={() => setDetailProduct(null)}
        onInquiry={setSelectedProduct}
      />

      <InquiryModal
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct}
      />
    </div>
  );
}
